import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { JourneyRole } from "../../enums/scan";
import { JOURNEY_ROLE } from "../../enums/scan";
import { isJourneyUtilityPage, journeyRoleOrder } from "./journeyRoles";
import type { JourneyLink } from "./journeyTypes";
import { routeClusterKey } from "./scanModel";

type JourneyGraph = {
  incoming: Map<string, JourneyLink[]>;
  outgoing: Map<string, JourneyLink[]>;
};

type JourneyPathSkeleton = {
  pageIds: string[];
  links: JourneyLink[];
};

export function buildJourneyGraph(data: OverviewResponse): JourneyGraph {
  const pageIds = new Set(data.pages.map((page) => page.id));
  const incoming = new Map<string, JourneyLink[]>();
  const outgoing = new Map<string, JourneyLink[]>();
  const seen = new Set<string>();
  for (const link of data.links) {
    if (
      link.fromPageId === link.toPageId ||
      !pageIds.has(link.fromPageId) ||
      !pageIds.has(link.toPageId)
    ) {
      continue;
    }
    const key = `${link.fromPageId}:${link.toPageId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    outgoing.set(link.fromPageId, [
      ...(outgoing.get(link.fromPageId) ?? []),
      link,
    ]);
    incoming.set(link.toPageId, [...(incoming.get(link.toPageId) ?? []), link]);
  }
  return { incoming, outgoing };
}

export function deriveRankedCandidatePathSkeletons(input: {
  data: OverviewResponse;
  graph: JourneyGraph;
  roles: Map<string, JourneyRole>;
  issueCounts: Map<string, number>;
  importantPageIds: Set<string>;
}): JourneyPathSkeleton[] {
  const pageById = new Map(input.data.pages.map((page) => [page.id, page]));
  const starts = [...input.data.pages]
    .filter((page) => input.roles.get(page.id) === JOURNEY_ROLE.ENTRY)
    .sort(
      (left, right) =>
        Number(right.path === "/") - Number(left.path === "/") ||
        edgeCount(input.graph.outgoing, right.id) -
          edgeCount(input.graph.outgoing, left.id) ||
        left.path.localeCompare(right.path),
    )
    .slice(0, 4);
  const sequences: JourneyPathSkeleton[] = [];
  const fallbacks: JourneyPathSkeleton[] = [];

  for (const start of starts) {
    walk([start.id], [], input, pageById, sequences, fallbacks);
    if (sequences.length >= 240) break;
  }

  const candidates = sequences.length > 0 ? sequences : fallbacks;
  const ranked = uniqueSkeletons(candidates).sort(
    (left, right) =>
      pathRank(right, input) - pathRank(left, input) ||
      journeyPathSignature(left).localeCompare(journeyPathSignature(right)),
  );
  return ranked;
}

export function selectLegacyPathSkeletons(
  ranked: JourneyPathSkeleton[],
): JourneyPathSkeleton[] {
  const selected: JourneyPathSkeleton[] = [];
  const terminalIds = new Set<string>();
  for (const candidate of ranked) {
    const terminalId = candidate.pageIds.at(-1);
    if (!terminalId || terminalIds.has(terminalId)) continue;
    selected.push(candidate);
    terminalIds.add(terminalId);
    if (selected.length === 4) break;
  }
  return selected;
}

export function journeyPathSignature(path: JourneyPathSkeleton): string {
  return path.pageIds.join(">");
}

function walk(
  pageIds: string[],
  links: JourneyLink[],
  input: Parameters<typeof deriveRankedCandidatePathSkeletons>[0],
  pageById: Map<string, OverviewResponse["pages"][number]>,
  sequences: JourneyPathSkeleton[],
  fallbacks: JourneyPathSkeleton[],
): void {
  if (sequences.length >= 240) return;
  const currentId = pageIds.at(-1);
  if (!currentId) return;
  const currentRole = input.roles.get(currentId) ?? JOURNEY_ROLE.UNCLASSIFIED;
  if (pageIds.length >= 2) fallbacks.push({ pageIds, links });
  const terminal =
    currentRole === JOURNEY_ROLE.ACTION ||
    currentRole === JOURNEY_ROLE.DESTINATION;
  if (pageIds.length >= 3 && (terminal || pageIds.length === 5)) {
    sequences.push({ pageIds, links });
  }
  if (terminal || pageIds.length === 5) return;

  const nextLinks = [...(input.graph.outgoing.get(currentId) ?? [])]
    .filter((link) => !pageIds.includes(link.toPageId))
    .filter((link) => {
      const page = pageById.get(link.toPageId);
      const role = input.roles.get(link.toPageId) ?? JOURNEY_ROLE.UNCLASSIFIED;
      return (
        page &&
        !isJourneyUtilityPage(page) &&
        role !== JOURNEY_ROLE.ENTRY &&
        journeyRoleOrder(role) >= journeyRoleOrder(currentRole)
      );
    })
    .sort(
      (left, right) => neighborRank(right, input) - neighborRank(left, input),
    )
    .slice(0, 7);
  for (const link of nextLinks) {
    walk(
      [...pageIds, link.toPageId],
      [...links, link],
      input,
      pageById,
      sequences,
      fallbacks,
    );
  }
}

function pathRank(
  path: JourneyPathSkeleton,
  input: Parameters<typeof deriveRankedCandidatePathSkeletons>[0],
): number {
  const roles = path.pageIds.map(
    (id) => input.roles.get(id) ?? JOURNEY_ROLE.UNCLASSIFIED,
  );
  const clusters = new Set(
    path.pageIds.map((id) => {
      const page = input.data.pages.find((item) => item.id === id);
      return page ? routeClusterKey(page.path) : "";
    }),
  );
  const finalRole = roles.at(-1);
  return (
    new Set(roles).size * 10 +
    clusters.size * 3 +
    path.pageIds.filter((id) => input.importantPageIds.has(id)).length * 2 +
    path.pageIds.filter((id) => (input.issueCounts.get(id) ?? 0) > 0).length +
    (finalRole === JOURNEY_ROLE.ACTION
      ? 8
      : finalRole === JOURNEY_ROLE.DESTINATION
        ? 5
        : 0) +
    (path.pageIds.length >= 3 && path.pageIds.length <= 5 ? 5 : 0)
  );
}

function neighborRank(
  link: JourneyLink,
  input: Parameters<typeof deriveRankedCandidatePathSkeletons>[0],
): number {
  const role = input.roles.get(link.toPageId) ?? JOURNEY_ROLE.UNCLASSIFIED;
  return (
    journeyRoleOrder(role) * 5 +
    Number(input.importantPageIds.has(link.toPageId)) * 4 +
    Math.min(input.issueCounts.get(link.toPageId) ?? 0, 3) +
    Number(link.agentBroken)
  );
}

function uniqueSkeletons(paths: JourneyPathSkeleton[]): JourneyPathSkeleton[] {
  return [
    ...new Map(
      paths.map((path) => [journeyPathSignature(path), path]),
    ).values(),
  ];
}

function edgeCount(map: Map<string, JourneyLink[]>, id: string): number {
  return map.get(id)?.length ?? 0;
}
